import { useEffect } from "react";

interface UseNavigationKeyboardProps {
  onNext?: () => void;
  onPrevious?: () => void;
  onTogglePalette?: () => void;
  onFlag?: () => void;
  onBookmark?: () => void;
  onReset?: () => void;
  enabled?: boolean;
}

export const useNavigationKeyboard = ({
  onNext,
  onPrevious,
  onTogglePalette,
  onFlag,
  onBookmark,
  onReset,
  enabled = true
}: UseNavigationKeyboardProps) => {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't trigger when user is typing in an input
      if (e.target instanceof HTMLInputElement || 
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement) {
        return;
      }

      // Prevent default for keyboard shortcuts
      const isShortcut = e.key === "ArrowLeft" || 
                        e.key === "ArrowRight" || 
                        (e.key === "p" && e.ctrlKey) ||
                        (e.key === "f" && e.ctrlKey) ||
                        (e.key === "b" && e.ctrlKey) ||
                        (e.key === "r" && e.ctrlKey);

      if (isShortcut) {
        e.preventDefault();
      }

      switch (e.key) {
        case "ArrowLeft":
          onPrevious?.();
          break;
        case "ArrowRight":
          onNext?.();
          break;
      }

      // Ctrl+key combinations
      if (e.ctrlKey) {
        switch (e.key) {
          case "p":
            onTogglePalette?.();
            break;
          case "f":
            onFlag?.();
            break;
          case "b":
            onBookmark?.();
            break;
          case "r":
            onReset?.();
            break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [enabled, onNext, onPrevious, onTogglePalette, onFlag, onBookmark, onReset]);

  // Return keyboard shortcuts info for UI display
  return {
    shortcuts: [
      { key: "←/→", description: "Navigate questions" },
      { key: "Ctrl+P", description: "Toggle palette" },
      { key: "Ctrl+F", description: "Flag question" },
      { key: "Ctrl+B", description: "Bookmark question" },
      { key: "Ctrl+R", description: "Reset question" }
    ]
  };
};